import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Beaker, Loader2, AlertCircle, ToggleLeft, ToggleRight } from "lucide-react";

const gbp = (p) => `£${((p || 0) / 100).toFixed(2)}`;

/**
 * v23.15 — Admin panel to flip the per-job `auto_charge_dry_run` flag on any
 * deposit job. When ON, the driver-clock-out auto-charge is logged but does
 * NOT hit Stripe. Ideal for E2E testing on the LIVE account without burning
 * real money.
 */
export default function AdminAutoChargeDryRunPanel() {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [flipping, setFlipping] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      // Reuse the existing escrow-jobs feed — that's where all deposit jobs live
      const { data } = await apiClient.get("/admin/escrow-jobs");
      const deposits = (data.jobs || []).filter((j) => j.payment_mode === "deposit");
      setJobs(deposits);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load deposit jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggle = async (job) => {
    setFlipping(job.id);
    try {
      const { data } = await apiClient.post(`/admin/jobs/${job.id}/toggle-dry-run`);
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, auto_charge_dry_run: data.auto_charge_dry_run } : j)));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to toggle dry-run");
    } finally {
      setFlipping(null);
    }
  };

  return (
    <section className="mb-12" data-testid="admin-dry-run-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <Beaker size={20} /> Auto-charge dry-run
          </h2>
          <p className="mt-1 text-sm text-[#475569] max-w-2xl">
            When a driver clocks out of a deposit job, we auto-charge the 70% balance to the customer&apos;s saved card. Toggle a job to <strong>Dry-run</strong> to log the charge without actually calling Stripe — perfect for E2E tests on this LIVE account.
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="dry-run-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading deposit jobs…</p>
      ) : jobs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A]/20 p-6 text-center text-sm text-[#475569]" data-testid="dry-run-empty">
          No deposit jobs yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="dry-run-list">
          {jobs.map((j) => (
            <div
              key={j.id}
              className={`bg-white border p-4 flex items-center gap-4 flex-wrap ${j.auto_charge_dry_run ? "border-[#F5A623]" : "border-[#0A0A0A]/10"}`}
              data-testid={`dry-run-row-${j.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <p className="text-xs font-semibold text-[#0A0A0A] truncate">{j.title || `Job ${j.id.slice(-6)}`}</p>
                <p className="mt-1 text-[11px] text-[#475569] inline-flex items-center gap-2">
                  <span>Status: {j.status}</span>
                  <span>·</span>
                  <span>Deposit {gbp(j.deposit_pence)} · Balance {gbp(j.balance_pence)}</span>
                  {j.balance_paid && <span className="text-[#2c8642]">· balance settled</span>}
                </p>
                {j.balance_auto_charge_dry_run && (
                  <p className="mt-1 text-[10px] uppercase tracking-[0.14em] font-bold text-[#A66800]" data-testid={`dry-run-fired-${j.id}`}>
                    Dry-run already fired · pm_kind={j.balance_auto_charge_pm_kind || "?"}
                  </p>
                )}
              </div>
              <button
                onClick={() => toggle(j)}
                disabled={flipping === j.id}
                data-testid={`dry-run-toggle-${j.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60 ${j.auto_charge_dry_run ? "bg-[#F5A623] text-white hover:bg-[#D48D1E]" : "bg-white border border-[#0A0A0A]/20 text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}
              >
                {flipping === j.id ? <Loader2 size={12} className="animate-spin" /> : (j.auto_charge_dry_run ? <ToggleRight size={14} /> : <ToggleLeft size={14} />)}
                {j.auto_charge_dry_run ? "Dry-run ON" : "Dry-run OFF"}
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
